import { useCallback, useEffect, useMemo, useState } from 'react';

import { LazyQueryHookOptions, QueryResult } from '@apollo/client';
import debounce from 'lodash/debounce';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import uniqBy from 'lodash/uniqBy';

import { getError } from '@spotify/confidence-graphql';

import { SearchUtils } from '../../utils';
import { useDocumentVisibilityDetection } from '../useDocumentVisibilityDetection';
import {
  PaginatedData,
  PaginatedListItem,
  PaginatedQuery,
  PaginatedVariables,
} from './types';
import { getPaginatedItems, getPaginatedListData } from './utils';

export const DEFAULT_AUTOCOMPLETE_SEARCH_FIELD = 'displayName';

export type UseAutocompleteOptions<
  TData extends PaginatedData = PaginatedData,
> = {
  search?: string;
  searchField?: string | string[];
  selected?: string | string[];
  query: PaginatedQuery<TData>;
  queryOptions?: LazyQueryHookOptions<TData> & { skip?: boolean };
};

type UseAutocompleteOptionsResult<TListItem extends PaginatedListItem> = {
  options: TListItem[];
  error?: Error;
  loading?: boolean;
  called?: boolean;
  fetchNextPage: () => void;
};

function getQueryError<TData extends PaginatedData>(
  result: QueryResult<TData, PaginatedVariables>,
) {
  return (
    getError(result.data) ||
    getError(getPaginatedListData(result.data)) ||
    result.error
  );
}

export function useAutocompleteOptions<
  TData extends PaginatedData,
  TListItem extends PaginatedListItem,
>({
  search,
  searchField = DEFAULT_AUTOCOMPLETE_SEARCH_FIELD,
  selected,
  query,
  queryOptions = {},
}: UseAutocompleteOptions<TData>): UseAutocompleteOptionsResult<TListItem> {
  const shouldFetch = !queryOptions?.skip;
  const { onCompleted } = queryOptions;
  const [refetching, setRefetching] = useState(false);
  const [options, setOptions] = useState<TListItem[]>([]);
  const mergeOptions = useCallback(
    (data: TData) => {
      const items = getPaginatedItems<TData, TListItem>(data) || [];
      if (items.length) {
        setOptions(currentOptions =>
          uniqBy(currentOptions.concat(items), 'name'),
        );
      }
      if (onCompleted) {
        onCompleted(data);
      }
    },
    [onCompleted],
  );

  // Separated into different requests in order to predictably combine the data into options
  const [paginatedFetch, paginatedQuery] = query(queryOptions);
  const [filteredFetch, filteredQuery] = query(queryOptions);
  const [selectedFetch, selectedQuery] = query({ onCompleted: mergeOptions });

  // Common error/loading state
  const queries = [paginatedQuery, filteredQuery, selectedQuery];
  const loading = refetching || queries.some(q => q.loading);
  const called = queries.some(q => q.called);
  const error = queries.map(getQueryError).find(Boolean);

  // If all pages have been succsssfully fetched we can avoid requests and rely on UI filtering
  const paginatedListData = getPaginatedListData<TData, TListItem>(
    paginatedQuery.data,
  );
  const hasMorePages = paginatedListData
    ? !!paginatedListData.nextPageToken
    : true;
  const preventAdditionalRequests = !shouldFetch || loading || !hasMorePages;

  // Populate initial options
  useEffect(() => {
    if (shouldFetch) {
      if (isEqual(paginatedQuery.variables, queryOptions.variables)) {
        paginatedFetch(queryOptions);
      } else {
        paginatedQuery.refetch(queryOptions);
      }
    }
    // Populate initial options once on mount; `queryOptions` is a fresh object
    // literal at most call sites, so honest deps would refetch every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Combine filter and search
  const createSearchFilter = useCallback(() => {
    const filterArray = [queryOptions?.variables?.filter || ''];

    if (search) {
      const searchFields = Array.isArray(searchField)
        ? searchField
        : [searchField];
      const searchFieldsFilter = searchFields
        .map(
          field =>
            `${field}:*${SearchUtils.escapeQuery(
              search,
            )}* OR ${field}:"${search}"`,
        )
        .join(' OR ');
      filterArray.push(`(${searchFieldsFilter})`);
    }
    return filterArray.filter(Boolean).join(' AND ').trim();
  }, [queryOptions?.variables?.filter, search, searchField]);

  // Enable infinite scrolling to gradually see all options
  const filteredListData = getPaginatedListData<TData, TListItem>(
    filteredQuery.data,
  );

  const { fetchMore: fetchMoreFiltered } = filteredQuery;
  const { fetchMore: fetchMorePaginated } = paginatedQuery;

  const fetchNextPage = useCallback(() => {
    if (!shouldFetch) {
      return;
    }
    if (search) {
      if (filteredListData?.nextPageToken) {
        fetchMoreFiltered({
          ...queryOptions,
          variables: {
            ...queryOptions.variables,
            filter: createSearchFilter(),
            pageToken: filteredListData.nextPageToken,
          },
        });
      }
    } else if (paginatedListData?.nextPageToken) {
      fetchMorePaginated({
        ...queryOptions,
        variables: {
          ...queryOptions.variables,
          pageToken: paginatedListData.nextPageToken,
        },
      });
    }
  }, [
    queryOptions,
    paginatedListData?.nextPageToken,
    filteredListData?.nextPageToken,
    search,
    shouldFetch,
    createSearchFilter,
    fetchMoreFiltered,
    fetchMorePaginated,
  ]);

  // onComplete does not exist on fetchMore (which enables pagination), so we need to merge these options manually
  useEffect(() => {
    if (paginatedQuery.data) {
      mergeOptions(paginatedQuery.data);
    }
  }, [paginatedQuery.data, mergeOptions]);

  useEffect(() => {
    if (filteredQuery.data) {
      mergeOptions(filteredQuery.data);
    }
  }, [filteredQuery.data, mergeOptions]);

  // Fetch potential additional results matching current filter
  const previousFilter = filteredQuery.variables?.filter;
  const fetchFiltered = useMemo(
    () =>
      debounce((_filter: string) => {
        filteredFetch({
          variables: {
            ...queryOptions.variables,
            filter: _filter,
          },
        });
      }, 500),
    [filteredFetch, queryOptions.variables],
  );

  useEffect(() => {
    if (preventAdditionalRequests) {
      return;
    }
    const filter = createSearchFilter();
    if (filter && previousFilter !== filter) {
      fetchFiltered(filter);
    }
  }, [
    preventAdditionalRequests,
    createSearchFilter,
    fetchFiltered,
    previousFilter,
  ]);

  // Make sure already selected items are fetched
  const createSelectedFilter = useCallback(() => {
    const allSelected = isArray(selected) ? selected : [selected];
    const missingSelected = allSelected.filter(
      s => !!s && !options.find(o => o.name === s),
    );
    if (missingSelected.length > 0) {
      return `name:(${missingSelected.map(name => `"${name}"`).join(' OR ')})`;
    }
    return undefined;
  }, [selected, options]);

  useEffect(() => {
    if (preventAdditionalRequests) {
      return;
    }
    const filter = createSelectedFilter();
    if (filter) {
      const hasAlreadyTried = selectedQuery.called && selectedQuery.data;
      const hasFilterChanged = selectedQuery.variables?.filter !== filter;

      if (hasAlreadyTried && !hasFilterChanged) {
        const allSelected = isArray(selected) ? selected : [selected];
        const isMissing = allSelected.some(
          s => !!s && !options.find(o => o.name === s),
        );
        if (isMissing) {
          return;
        }
      } else {
        selectedFetch({
          variables: {
            filter,
          },
        });
      }
    }
  }, [
    preventAdditionalRequests,
    shouldFetch,
    createSelectedFilter,
    selectedFetch,
    selected,
    options,
    selectedQuery.called,
    selectedQuery.data,
    selectedQuery.variables?.filter,
  ]);

  const { refetch: refetchPaginated } = paginatedQuery;
  const { refetch: refetchFiltered } = filteredQuery;
  const { refetch: refetchSelected } = selectedQuery;

  const refetch = useCallback(async () => {
    if (shouldFetch) {
      await refetchPaginated();
      if (filteredQuery.data) {
        const filter = createSearchFilter();
        if (filter) {
          await refetchFiltered({ filter });
        }
      }
      if (selectedQuery.data) {
        const filter = createSelectedFilter();
        if (filter) {
          await refetchSelected({ filter });
        }
      }
    }
  }, [
    shouldFetch,
    refetchPaginated,
    refetchFiltered,
    refetchSelected,
    filteredQuery.data,
    selectedQuery.data,
    createSearchFilter,
    createSelectedFilter,
  ]);

  // Reset options when passed down filter changes
  const previousItemFilter = paginatedQuery.variables?.filter;
  const itemFilter = queryOptions?.variables?.filter;
  useEffect(() => {
    if (itemFilter && previousItemFilter !== itemFilter) {
      setOptions([]);
      if (shouldFetch) {
        paginatedFetch(queryOptions);
      }
    }
  }, [
    itemFilter,
    previousItemFilter,
    shouldFetch,
    paginatedFetch,
    queryOptions,
  ]);

  // Trigger refetch when tab becomes visible
  const handleRefetch = useCallback(
    async (isDocumentVisible: boolean) => {
      if (isDocumentVisible) {
        setRefetching(true);
        await refetch();
        setRefetching(false);
      }
    },
    [refetch],
  );

  useDocumentVisibilityDetection(handleRefetch);

  return {
    loading,
    called,
    options,
    error,
    fetchNextPage,
  };
}
